import React, { useState, useEffect, useRef } from "react";
import { Button, Form, Row, Col, Table } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Marker,
  Popup,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import { MultiSelect } from "primereact/multiselect";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { MapHomeControl, MapInfoControl, MapLegendControl } from "../../../../common/MapControls/MapControls";

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

const CtrlKeyZoom = () => {
  const [, setIsCtrlPressed] = useState(false);
  const map = useMapEvents({
    zoom() {},
    keydown(event) {
      if (event.key === "Control") {
        setIsCtrlPressed(true);
        map.scrollWheelZoom.enable();
      }
    },
    keyup(event) {
      if (event.key === "Control") {
        setIsCtrlPressed(false);
        map.scrollWheelZoom.disable();
      }
    },
  });

  useEffect(() => {
    map.scrollWheelZoom.disable();
    return () => {
      map.scrollWheelZoom.enable();
    };
  }, [map]);

  return null;
};

const Blackspotmap = () => {
  const navigate = useNavigate();
  const handleclickbackdashboard = () => {
    navigate("/Roadaccident/Dashboard");
  };

  const [selectedYears, setSelectedYears] = useState([]);
  const years = [
    { name: "2025", value: "2025" },
    { name: "2024", value: "2024" },
    { name: "2023", value: "2023" },
    { name: "2022", value: "2022" },
    { name: "2021", value: "2021" },
    { name: "2020", value: "2020" },
  ];

  const [legendVisible, setLegendVisible] = useState(false);
  const legendRef = useRef(null);

  const [geojsonData, setGeojsonData] = useState(null);
  useEffect(() => {
    fetch("/DATA/OSMbased_adm0_boundary.json")
      .then((response) => response.json())
      .then((data) => setGeojsonData(data))
      .catch((error) => console.error("Error loading boundary data:", error));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (legendRef.current && !legendRef.current.contains(event.target)) {
        setLegendVisible(false);
      }
    };

    if (legendVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [legendVisible]);

  const [markerPositions, setMarkerPositions] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const handlevlackspotlocationSearch = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!selectedYears || selectedYears.length === 0) {
      setErrorMessage("Please select at least one year.");
      setMarkerPositions([]);
      setFilterData([]);
      return;
    }

    const yearsInt = selectedYears.map((year) => parseInt(year, 10));

    try {
      const response = await fetch(
        `${backendUrl}roadaccident/filter_blackspot_details/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ year: yearsInt }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        setErrorMessage(data.error || "Error fetching data");
        setMarkerPositions([]);
        setFilterData([]);
        return;
      }

      if (data.length === 0) {
        setErrorMessage("Data not available");
        setMarkerPositions([]);
        setFilterData([]);
        return;
      }

      const markers = data.map((record) => ({
        lat: parseFloat(record.latitude),
        lng: parseFloat(record.longitude),
        label: record.location || "Blackspot",
      }));
      setMarkerPositions(markers);
      setFilterData(data);
      setCurrentPage(1); // Reset to first page when new data is fetched
    } catch (error) {
      console.error("Error fetching data:", error);
      setErrorMessage("Error fetching data");
      setMarkerPositions([]);
      setFilterData([]);
    }
  };

  // Filter data based on search query
  const filteredData = filterData.filter((item) =>
    [
      item.year.toString(),
      item.municipality,
      item.submunicipality,
      item.suco,
      item.roadtype,
      item.location,
      item.latitude?.toString() || "-",
      item.longitude?.toString() || "-",
      item.datetime,
      item.numberofaccidents.toString(),
      item.numberofinjured.toString(),
      item.numberoffatal.toString(),
    ].some((field) =>
      field?.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const totalPages = Math.ceil(filteredData.length / entriesPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  const geoJsonStyle = {
    color: "red",
    weight: 1.4,
    fillOpacity: 0,
  };

  const mapCenter = [-9.1747, 125.7275];

  return (
    <div className="">
      <div className="formcontainer1" style={{ marginBottom: "15px" }}>
        <Form onSubmit={handlevlackspotlocationSearch}>
          <Row className="align-items-center">
            <Col md={5}>
              <Form.Group className="d-flex align-items-center gap-3">
                <Form.Label className="mb-0">Year</Form.Label>
                <MultiSelect
                  value={selectedYears}
                  onChange={(e) => setSelectedYears(e.value)}
                  options={years}
                  optionLabel="name"
                  display="chip"
                  placeholder="Select Year(s)"
                  className="w-full md:w-20rem"
                  style={{ width: "400px", fontSize: "13px", height: "40px" }}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Button variant="primary" type="submit">
                Search
              </Button>
            </Col>
            <Col md={3}>
              <Button variant="success" onClick={handleclickbackdashboard}>
                Return to Dashboard
              </Button>
            </Col>
          </Row>
        </Form>
        {errorMessage && (
          <div style={{ marginTop: "10px", color: "red" }}>
            {errorMessage}
          </div>
        )}
      </div>

      <div className="Blackspotpart2">
        <MapContainer center={mapCenter} zoom={8} className="leaflet-map">
          <MapHomeControl />
          <MapInfoControl text="Identified accident blackspots on the road network. Click a marker to view details." />
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="© OpenStreetMap contributors"
          />
          {geojsonData && <GeoJSON data={geojsonData} style={geoJsonStyle} />}
          {markerPositions.map((marker, index) => (
            <Marker
              key={index}
              position={[marker.lat, marker.lng]}
              icon={L.icon({
                iconUrl: "/Roadaccidenticons/black.png",
                iconSize: [20, 20],
              })}
            >
              <Popup>{marker.label}</Popup>
            </Marker>
          ))}
          <CtrlKeyZoom />
          <MapLegendControl
            sections={[{
              items: [
                { label: "Black Spot", icon: <img src="/Roadaccidenticons/black.png" alt="Icon" style={{ width: "18px", height: "18px" }} /> },
              ],
            }]}
          />
        </MapContainer>
      </div>

      {filterData.length > 0 && (
        <div className="formcontainer1" style={{ marginTop: "20px" }}>
          <div className="d-flex justify-content-between mb-3">
            <div className="d-flex align-items-center gap-3">
              <select
                className="form-select"
                style={{ width: "70px" }}
                value={entriesPerPage}
                onChange={(e) => setEntriesPerPage(Number(e.target.value))}
              >
                {[10, 25, 50, 100].map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
              <p className="mb-0">entries per page</p>
            </div>
            <input
              type="text"
              className="form-control"
              placeholder="Search"
              style={{ width: "200px" }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <Table bordered hover>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Year</th>
                <th>Municipality</th>
                <th>Sub Municipality</th>
                <th>Suco</th>
                <th>Road Type</th>
                <th>Location</th>
                <th>Latitude</th>
                <th>Longitude</th>
                <th>Date & Time</th>
                <th>No. of Accidents</th>
                <th>No. of Injured</th>
                <th>No. of Fatalities</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => (
                <tr key={index}>
                  <td>{filterData.indexOf(item) + 1}</td>
                  <td>{item.year}</td>
                  <td>{item.municipality}</td>
                  <td>{item.submunicipality}</td>
                  <td>{item.suco}</td>
                  <td>{item.roadtype}</td>
                  <td>{item.location}</td>
                  <td>{item.latitude || "-"}</td>
                  <td>{item.longitude || "-"}</td>
                  <td>{new Date(item.datetime).toLocaleString()}</td>
                  <td>{item.numberofaccidents}</td>
                  <td>{item.numberofinjured}</td>
                  <td>{item.numberoffatal}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="d-flex justify-content-between align-items-center">
            <p>
              Showing {Math.min((currentPage - 1) * entriesPerPage + 1, filteredData.length)} to{" "}
              {Math.min(currentPage * entriesPerPage, filteredData.length)} of {filteredData.length} entries
            </p>
            <nav>
              <ul className="pagination">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}>
                    Previous
                  </button>
                </li>
                {[...Array(totalPages).keys()].map((page) => (
                  <li key={page} className={`page-item ${currentPage === page + 1 ? "active" : ""}`}>
                    <button className="page-link" onClick={() => setCurrentPage(page + 1)}>
                      {page + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}>
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default Blackspotmap;